import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Plus,
  RotateCw,
  UserPlus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnvironmentBadge, KybLock, PageHead, Panel } from "@/components/bits";
import { CountrySelect } from "@/components/country-select";
import { DialCodeSelect } from "@/components/dial-code-select";
import { countryInfo } from "@/lib/countries";
import { useApiResource } from "@/hooks/use-api-resource";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { useI18n } from "@/i18n/i18n-context";
import { publicApi } from "@/services/api-client";
import {
  Chip,
  Empty,
  ErrorBox,
  MetaList,
  Skeleton,
  errorMessage,
  list,
  rec,
  txt,
  type Rec,
} from "@/features/onboarding/shared";

const EMPTY_HOLDER = {
  first_name: "",
  last_name: "",
  email: "",
  country_code: "+237",
  contact: "",
  dob: "",
  billing_name: "",
  is_business: false,
  business_name: "",
  billing_address: "",
  billing_city: "",
  billing_country: "CM",
  billing_state: "",
  billing_postal_code: "",
  id_number: "",
};
type Holder = typeof EMPTY_HOLDER;
const HOLDER_FIELDS: [keyof Holder, string, string?][] = [
  ["first_name", "Prénom"],
  ["last_name", "Nom"],
  ["email", "E-mail", "email"],
  ["country_code", "Indicatif pays (ex: +237)"],
  ["contact", "Téléphone", "tel"],
  ["dob", "Date de naissance", "date"],
  ["billing_address", "Adresse"],
  ["billing_city", "Ville"],
  ["billing_state", "Région"],
  ["billing_country", "Pays (ISO-2)"],
  ["billing_postal_code", "Code postal"],
  ["id_number", "Numéro de pièce d'identité"],
];

export default function CardsPage() {
  const { t } = useI18n();
  const d = useDashboard();
  const blocked = (d.isBusiness && d.locked) || !d.financialAllowed;
  const [userPage, setUserPage] = useState(1);
  const [cardPage, setCardPage] = useState(1);
  const users = useApiResource<Rec>(
    blocked ? null : `/virtual-card-users?per_page=25&page=${userPage}`,
  );
  const cards = useApiResource<Rec>(
    blocked ? null : `/virtual-cards?per_page=25&page=${cardPage}`,
  );
  const [holderOpen, setHolderOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [customer, setCustomer] = useState<string | null>(null);

  if (blocked) {
    return (
      <>
        <PageHead title={t("cards.title")} sub={t("cards.subBlocked")} />
        {d.isBusiness ? (
          <KybLock what={t("cards.title")} />
        ) : (
          <Panel>
            <Empty
              title={t("cards.unavailableTitle")}
              sub={t("cards.unavailableSub")}
            />
          </Panel>
        )}
      </>
    );
  }

  const userRows = list(users.data, ["customers", "users"]);
  const cardRows = list(cards.data, ["cards"]);
  const cardQuota = rec(rec(cards.meta).card_creation_limit);
  const merchantCardLimit = num(cardQuota.maximum);
  const merchantCardsUsed = num(cardQuota.used);
  const merchantCardLimitReached =
    d.isBusiness &&
    merchantCardLimit !== undefined &&
    merchantCardsUsed !== undefined &&
    merchantCardsUsed >= merchantCardLimit;

  return (
    <>
      <PageHead title={t("cards.title")} sub={t("cards.subSensitive")}>
        <EnvironmentBadge />
        <Button
          variant="outline"
          data-testid="button-new-cardholder"
          onClick={() => setHolderOpen(true)}
        >
          <UserPlus className="mr-1.5 h-4 w-4" />
          {t("cards.cardholderBtn")}
        </Button>
        <Button
          data-testid="button-new-card"
          disabled={!userRows.length || merchantCardLimitReached}
          title={
            merchantCardLimitReached
              ? "La limite de 3 cartes virtuelles est atteinte."
              : undefined
          }
          onClick={() => setIssueOpen(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {t("cards.issueCardBtn")}
        </Button>
        {d.isBusiness &&
          merchantCardLimit !== undefined &&
          merchantCardsUsed !== undefined && (
            <span className="text-xs text-muted-foreground">
              {merchantCardLimitReached
                ? `Limite atteinte : ${merchantCardsUsed}/${merchantCardLimit} cartes`
                : `${merchantCardsUsed}/${merchantCardLimit} cartes utilisées`}
            </span>
          )}
      </PageHead>
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <Panel
          title={t("cards.cardsPanel")}
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void cards.reload()}
            >
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
          }
        >
          {cards.loading ? (
            <Skeleton />
          ) : cards.error ? (
            <ErrorBox
              message={cards.error}
              onRetry={() => void cards.reload()}
            />
          ) : cardRows.length === 0 ? (
            <Empty
              title={t("cards.emptyCards")}
              sub={t("cards.emptyCardsSub")}
            />
          ) : (
            <ul className="space-y-2" data-testid="list-cards">
              {cardRows.map((c) => (
                <li
                  key={txt(c.id)}
                  className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
                >
                  <CreditCard className="h-5 w-5 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {txt(c.category, t("cards.cardFallback"))} ·{" "}
                      {txt(c.grade, "")}
                    </p>
                    <p className="truncate font-mono text-[11px] text-muted-foreground">
                      {txt(c.id)}
                    </p>
                  </div>
                  <Chip value={c.status} />
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`button-open-card-${txt(c.id)}`}
                    onClick={() => setOpenCard(txt(c.id))}
                  >
                    {t("cards.openCardBtn")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Pager
            id="cards"
            page={cardPage}
            info={pageInfo(cards.data, cards.meta)}
            loading={cards.loading}
            onPage={setCardPage}
          />
        </Panel>
        <Panel
          title={t("cards.cardholdersPanel")}
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void users.reload()}
            >
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
          }
        >
          {users.loading ? (
            <Skeleton rows={2} />
          ) : users.error ? (
            <ErrorBox
              message={users.error}
              onRetry={() => void users.reload()}
            />
          ) : userRows.length === 0 ? (
            <Empty
              title={t("cards.emptyHolders")}
              sub={t("cards.emptyHoldersSub")}
            />
          ) : (
            <ul className="space-y-2" data-testid="list-cardholders">
              {userRows.map((u) => (
                <li
                  key={txt(u.id)}
                  className="flex items-center gap-2 rounded-lg border p-2.5 text-xs"
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="block truncate font-semibold">
                      {txt(u.display_name) ||
                        `${txt(u.first_name)} ${txt(u.last_name)}`.trim() ||
                        "Titulaire"}
                    </span>
                    <span className="block truncate font-mono text-[10px] text-muted-foreground">
                      {txt(u.reference, txt(u.provider_reference, "—"))}
                    </span>
                  </span>
                  <Chip value={u.status ?? "ACTIVE"} />
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`button-holder-${txt(u.id)}`}
                    onClick={() => setCustomer(txt(u.id))}
                  >
                    Voir
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Pager
            id="cardholders"
            page={userPage}
            info={pageInfo(users.data, users.meta)}
            loading={users.loading}
            onPage={setUserPage}
          />
        </Panel>
      </div>
      <HolderDialog
        open={holderOpen}
        onClose={() => setHolderOpen(false)}
        onCreated={() => void users.reload()}
      />
      <IssueDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        customers={userRows}
        onCreated={() => void cards.reload()}
      />
      {customer && (
        <CustomerDialog id={customer} onClose={() => setCustomer(null)} />
      )}
      {openCard && (
        <CardDetail
          id={openCard}
          onClose={() => setOpenCard(null)}
          onChanged={() => void cards.reload()}
        />
      )}
    </>
  );
}

type PageInfo = {
  current?: number;
  last?: number;
  total?: number;
  hasNext: boolean;
};
const num = (v: unknown) =>
  typeof v === "number" ||
  (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)))
    ? Number(v)
    : undefined;
/** Reads Laravel paginator fields from data, data.meta or the envelope meta. Never invents totals. */
function pageInfo(data: unknown, meta: unknown): PageInfo {
  const sources = [
    rec(data),
    rec(rec(data).meta),
    rec(rec(data).pagination),
    rec(meta),
    rec(rec(meta).pagination),
  ];
  const pick = (k: string) => {
    for (const o of sources) {
      const n = num(o[k]);
      if (n !== undefined) return n;
    }
    return undefined;
  };
  const current = pick("current_page");
  const last = pick("last_page");
  const total = pick("total");
  const nextUrl =
    sources.some(
      (o) => typeof o.next_page_url === "string" && o.next_page_url,
    ) ||
    sources.some(
      (o) => typeof rec(o.links).next === "string" && rec(o.links).next,
    );
  const hasNext =
    current !== undefined && last !== undefined ? current < last : nextUrl;
  return { current, last, total, hasNext: Boolean(hasNext) };
}

function Pager({
  id,
  page,
  info,
  loading,
  onPage,
}: {
  id: string;
  page: number;
  info: PageInfo;
  loading: boolean;
  onPage: (p: number) => void;
}) {
  if (page === 1 && !info.hasNext) return null;
  return (
    <div
      className="mt-3 flex items-center justify-between gap-2 text-xs"
      data-testid={`pagination-${id}`}
    >
      <Button
        size="sm"
        variant="outline"
        data-testid={`button-${id}-prev`}
        disabled={loading || page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
      >
        Précédent
      </Button>
      <span className="text-muted-foreground" data-testid={`text-${id}-page`}>
        Page {info.current ?? page}
        {info.last !== undefined ? ` sur ${info.last}` : ""}
        {info.total !== undefined ? ` · ${info.total} au total` : ""}
      </span>
      <Button
        size="sm"
        variant="outline"
        data-testid={`button-${id}-next`}
        disabled={loading || !info.hasNext}
        onClick={() => onPage(page + 1)}
      >
        Suivant
      </Button>
    </div>
  );
}

function HolderDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [h, setH] = useState<Holder>(EMPTY_HOLDER);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<Rec | null>(null);
  const close = () => {
    setH(EMPTY_HOLDER);
    setError("");
    setResult(null);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const missing = HOLDER_FIELDS.filter(([k]) => !String(h[k]).trim());
    if (missing.length)
      return setError(`Champs requis : ${missing.map((m) => m[1]).join(", ")}`);
    if (h.is_business && !h.business_name.trim())
      return setError("Nom de l'entreprise requis.");
    setBusy(true);
    setError("");
    const countryCode = h.country_code.trim();
    const formattedCountryCode = countryCode.startsWith("+")
      ? countryCode
      : `+${countryCode.replace(/^0+/, "")}`;
    const autoBillingName =
      `${h.first_name.trim()} ${h.last_name.trim()}`.trim();
    try {
      const r = await publicApi<Rec>("/virtual-card-users", {
        method: "POST",
        body: {
          ...h,
          country_code: formattedCountryCode,
          billing_name: autoBillingName,
          business_name: h.is_business ? h.business_name.trim() : "null",
        },
      });
      setH(EMPTY_HOLDER);
      setResult(rec(rec(r.data).customer ?? r.data));
      onCreated();
    } catch (c) {
      setError(errorMessage(c));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nouveau titulaire de carte</DialogTitle>
          <DialogDescription>
            Ces informations sont transmises au fournisseur et ne sont pas
            conservées par BizConnectPay.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="panel-holder-created">
            <p className="text-sm font-semibold">Titulaire créé</p>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="font-semibold">
                {txt(result.display_name) ||
                  `${txt(result.first_name)} ${txt(result.last_name)}`.trim() ||
                  "Titulaire"}
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                Référence :{" "}
                {txt(result.provider_reference) || txt(result.reference, "—")}
              </p>
            </div>
            <Button onClick={close}>Fermer</Button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="grid gap-3 sm:grid-cols-2"
            noValidate
            data-testid="form-cardholder"
          >
            {error && (
              <div className="sm:col-span-2">
                <ErrorBox message={error} />
              </div>
            )}
            {HOLDER_FIELDS.map(([k, label, type]) => {
              if (k === "billing_country") {
                return (
                  <div key={k} className="grid gap-1">
                    <Label htmlFor={`h-${k}`} className="text-xs">
                      {label}
                    </Label>
                    <CountrySelect
                      id={`h-${k}`}
                      value={h.billing_country}
                      onValueChange={(code) => {
                        const country = countryInfo(code);
                        const dial = country?.dial
                          ? country.dial.replace(/^\+/, "").trim()
                          : "";
                        setH((prev) => ({
                          ...prev,
                          billing_country: code,
                          ...(dial ? { country_code: dial } : {}),
                        }));
                      }}
                      testId={`input-holder-${k}`}
                    />
                  </div>
                );
              }
              if (k === "country_code") {
                return (
                  <div key={k} className="grid gap-1">
                    <Label htmlFor={`h-${k}`} className="text-xs">
                      {label}
                    </Label>
                    <DialCodeSelect
                      id={`h-${k}`}
                      value={h.country_code}
                      onValueChange={(dial) =>
                        setH((prev) => ({ ...prev, country_code: dial }))
                      }
                      testId={`input-holder-${k}`}
                    />
                  </div>
                );
              }
              return (
                <div key={k} className="grid gap-1">
                  <Label htmlFor={`h-${k}`} className="text-xs">
                    {label}
                  </Label>
                  <Input
                    id={`h-${k}`}
                    type={type ?? "text"}
                    autoComplete="off"
                    data-testid={`input-holder-${k}`}
                    value={String(h[k])}
                    onChange={(e) => setH({ ...h, [k]: e.target.value })}
                  />
                </div>
              );
            })}
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch
                id="h-biz"
                checked={h.is_business}
                data-testid="switch-holder-business"
                onCheckedChange={(v) => setH({ ...h, is_business: v })}
              />
              <Label htmlFor="h-biz">Titulaire entreprise</Label>
            </div>
            {h.is_business && (
              <div className="grid gap-1 sm:col-span-2">
                <Label htmlFor="h-bn" className="text-xs">
                  Nom de l'entreprise
                </Label>
                <Input
                  id="h-bn"
                  data-testid="input-holder-business_name"
                  value={h.business_name}
                  onChange={(e) =>
                    setH({ ...h, business_name: e.target.value })
                  }
                />
              </div>
            )}
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={close}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="button-create-cardholder"
              >
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Créer le titulaire
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CustomerDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [data, setData] = useState<Rec | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const ctrl = useRef<AbortController | null>(null);

  const wipe = useCallback(() => {
    ctrl.current?.abort();
    ctrl.current = null;
    setData(null);
    setLoading(false);
    setRevealed(false);
  }, []);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const ac = new AbortController();
    ctrl.current = ac;
    setLoading(true);
    setError("");
    setCleared(false);
    try {
      const r = await publicApi<Rec>(
        `/virtual-card-users/${encodeURIComponent(id)}`,
        { cache: "no-store", signal: ac.signal },
      );
      if (ac.signal.aborted || ctrl.current !== ac) return;
      setData(rec(r.data));
    } catch (e) {
      if (!ac.signal.aborted && ctrl.current === ac)
        setError(errorMessage(e, "Impossible de charger le titulaire."));
    } finally {
      if (ctrl.current === ac) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
    const hide = () => {
      wipe();
      setCleared(true);
    };
    const vis = () => {
      if (document.visibilityState === "hidden") hide();
    };
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", vis);
    return () => {
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", vis);
      wipe();
    };
  }, [load, wipe]);

  const close = () => {
    wipe();
    onClose();
  };

  const raw = rec(data);
  const user = rec(
    raw.user ??
      (Array.isArray(raw.users)
        ? raw.users[0]
        : Array.isArray(raw.cards)
          ? {}
          : raw),
  );
  const cards: Rec[] = Array.isArray(raw.cards) ? (raw.cards as Rec[]) : [];

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto sm:max-w-xl"
        data-testid="dialog-cardholder-detail"
      >
        <DialogHeader>
          <DialogTitle>Titulaire &amp; Cartes associées</DialogTitle>
          <DialogDescription>
            Données fournisseur en direct, non mises en cache.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <ErrorBox message={error} onRetry={() => void load()} />
        ) : data ? (
          <div className="space-y-5">
            {/* Titulaire / Cardholder Section */}
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between border-b pb-2">
                <div>
                  <h4 className="text-sm font-semibold">
                    {txt(user.billing_name) ||
                      `${txt(user.first_name)} ${txt(user.last_name)}`.trim() ||
                      "Titulaire"}
                  </h4>
                  <p className="font-mono text-xs text-muted-foreground">
                    Réf : {txt(user.reference ?? id)}
                  </p>
                </div>
                <Chip value={user.is_business ? "PROFESSIONAL" : "PERSONAL"} />
              </div>

              <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="font-medium">{txt(user.email, "—")}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Contact</dt>
                  <dd className="font-medium">
                    {user.country_code ? `${user.country_code} ` : ""}
                    {txt(user.contact, "—")}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Date de naissance</dt>
                  <dd className="font-medium">
                    {user.dob ? String(user.dob).slice(0, 10) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Numéro d'identité</dt>
                  <dd className="font-medium">{txt(user.id_number, "—")}</dd>
                </div>
                {Boolean(
                  user.business_name &&
                  user.business_name !== "null" &&
                  user.business_name !== "NAN",
                ) && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">Entreprise</dt>
                    <dd className="font-medium">{txt(user.business_name)}</dd>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <dt className="text-muted-foreground">
                    Adresse de facturation
                  </dt>
                  <dd className="font-medium">
                    {[
                      user.billing_address,
                      user.billing_city,
                      user.billing_state,
                      user.billing_country,
                      user.billing_postal_code,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Cartes associées */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold">
                    Cartes associées ({cards.length})
                  </h4>
                </div>
                {cards.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setRevealed((v) => !v)}
                  >
                    {revealed ? (
                      <>
                        <EyeOff className="mr-1.5 h-3.5 w-3.5" />
                        Masquer les codes
                      </>
                    ) : (
                      <>
                        <Eye className="mr-1.5 h-3.5 w-3.5" />
                        Afficher les codes
                      </>
                    )}
                  </Button>
                )}
              </div>

              {cards.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                  Aucune carte associée à ce titulaire pour le moment.
                </div>
              ) : (
                <div className="space-y-3" data-testid="cardholder-cards-list">
                  {cards.map((c, i) => (
                    <div
                      key={txt(c.id ?? c.card_id ?? i)}
                      className="rounded-lg border p-3 space-y-2 bg-card"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-xs">
                            {txt(c.card_type ?? "VISA")} ·{" "}
                            {txt(c.grade ?? "PREMIUM")}
                          </span>
                          <Chip value={c.status} />
                        </div>
                        <span className="font-mono text-xs font-semibold">
                          {c.balance !== undefined
                            ? `${c.balance} ${txt(c.currency, "USD")}`
                            : ""}
                        </span>
                      </div>

                      <div className="flex items-center justify-between rounded bg-muted/50 px-3 py-1.5 font-mono text-xs">
                        <span className="tracking-wider">
                          {revealed
                            ? txt(c.card_pan, txt(c.masked_pan))
                            : txt(c.masked_pan, "**** **** **** ****")}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Exp: {txt(c.expired_at, "—")}
                        </span>
                      </div>

                      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                        <div>
                          <dt className="text-muted-foreground inline">
                            CVV :{" "}
                          </dt>
                          <dd className="inline font-mono font-medium">
                            {revealed ? txt(c.cvv, "—") : "***"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground inline">
                            PIN :{" "}
                          </dt>
                          <dd className="inline font-mono font-medium">
                            {revealed ? txt(c.pin, "—") : "******"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground inline">
                            Catégorie :{" "}
                          </dt>
                          <dd className="inline font-medium">
                            {txt(c.category, "PERSONAL")}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground inline">
                            Active :{" "}
                          </dt>
                          <dd className="inline font-medium">
                            {c.active ? "Oui" : "Non"}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : loading ? (
          <Skeleton rows={2} />
        ) : (
          <div className="space-y-2">
            {cleared && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="text-cardholder-cleared"
              >
                Détails effacés car la page a perdu le focus.
              </p>
            )}
            <Button
              size="sm"
              data-testid="button-reload-cardholder"
              onClick={() => void load()}
            >
              Afficher à nouveau
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({
  open,
  onClose,
  customers,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  customers: Rec[];
  onCreated: () => void;
}) {
  const d = useDashboard();
  const idem = useIdempotencyKey();
  const [f, setF] = useState({
    customer_id: "",
    initial_balance: "10",
    card_type: "VISA",
    category: "PERSONAL",
    grade: "PREMIUM",
  });
  const [quote, setQuote] = useState<{
    data: Rec | null;
    loading: boolean;
    error: string;
  }>({ data: null, loading: false, error: "" });
  const [quoteNonce, setQuoteNonce] = useState(0);
  useEffect(() => {
    if (!open) {
      setQuote({ data: null, loading: false, error: "" });
      return;
    }
    const ac = new AbortController();
    setQuote({ data: null, loading: true, error: "" });
    publicApi<Rec>("/virtual-cards/quote", {
      cache: "no-store",
      signal: ac.signal,
    })
      .then((r) => {
        if (!ac.signal.aborted)
          setQuote({ data: rec(r.data), loading: false, error: "" });
      })
      .catch((e) => {
        if (!ac.signal.aborted)
          setQuote({ data: null, loading: false, error: errorMessage(e) });
      });
    return () => ac.abort();
  }, [open, quoteNonce]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<Rec | null>(null);
  const close = () => {
    setConfirm(false);
    setError("");
    setResult(null);
    onClose();
  };

  const issue = async () => {
    const payload = {
      customer_id: f.customer_id,
      debit_currency: txt(rec(q.selected_wallet).currency, ""),
      initial_balance: f.initial_balance,
      balance: f.initial_balance,
      currency: "USD",
      card_type: f.card_type,
      category: f.category,
      grade: "PREMIUM",
    };
    setBusy(true);
    setError("");
    try {
      const r = await publicApi<Rec>("/virtual-cards", {
        method: "POST",
        body: { ...payload, idempotency_key: idem.forPayload(payload) },
      });
      idem.reset();
      setResult(rec(r.data));
      setConfirm(false);
      onCreated();
      void d.refresh();
    } catch (c) {
      setError(errorMessage(c));
    } finally {
      setBusy(false);
    }
  };

  const q = rec(rec(quote.data).quote ?? quote.data);
  const selectedWallet = rec(q.selected_wallet);
  const debit = txt(selectedWallet.currency, "");
  const valid =
    f.customer_id &&
    selectedWallet.id &&
    /^\d+(\.\d{1,2})?$/.test(f.initial_balance);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Émettre une carte virtuelle</DialogTitle>
          <DialogDescription>
            Les frais sont réservés sur votre wallet avant l'appel fournisseur,
            puis capturés ou libérés.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="panel-card-created">
            <p className="text-sm font-semibold">Demande traitée</p>
            <MetaList data={rec(result.card ?? result)} />
            {Object.keys(rec(result.reservation)).length > 0 && (
              <>
                <p className="text-xs font-semibold">Réservation</p>
                <MetaList data={rec(result.reservation)} />
              </>
            )}
            {result.reservation_status !== undefined && (
              <p className="text-xs">
                Réservation : <Chip value={result.reservation_status} />
              </p>
            )}
            <Button onClick={close}>Fermer</Button>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="form-issue-card">
            {error && <ErrorBox message={error} />}
            <div className="grid gap-1">
              <Label>Titulaire</Label>
              <Select
                value={f.customer_id}
                onValueChange={(v) => setF({ ...f, customer_id: v })}
              >
                <SelectTrigger data-testid="select-card-customer">
                  <SelectValue placeholder="Choisir" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={txt(c.id)} value={txt(c.id)}>
                      {txt(c.display_name) ||
                        `${txt(c.first_name)} ${txt(c.last_name)}`.trim() ||
                        "Titulaire"}{" "}
                      · {txt(c.reference, txt(c.provider_reference, "—"))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              className="rounded-lg bg-accent/50 p-3 text-xs"
              data-testid="panel-card-quote"
            >
              {quote.loading ? (
                <Skeleton rows={1} />
              ) : quote.error ? (
                <ErrorBox
                  message={quote.error}
                  onRetry={() => setQuoteNonce((n) => n + 1)}
                />
              ) : (
                <div className="space-y-2">
                  <div className="rounded-md border bg-background p-2">
                    <p className="font-semibold">
                      Wallet sélectionné : {debit || "—"} · solde disponible{" "}
                      {txt(selectedWallet.available_balance, "—")} {debit}
                    </p>
                    <p className="font-semibold">
                      Frais de création : {txt(q.card_creation_fee_usd, "—")}{" "}
                      USD
                    </p>
                    <p className="text-muted-foreground">
                      Taux appliqué : {txt(rec(q.exchange_rate).display, "—")}
                    </p>
                    <p className="text-muted-foreground">
                      Débit estimé : {txt(rec(q.wallet_debit).amount, "—")}{" "}
                      {txt(rec(q.wallet_debit).currency, debit)}
                    </p>
                  </div>
                  <MetaList data={q} />
                </div>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label>Solde initial (USD)</Label>
                <Input
                  inputMode="decimal"
                  data-testid="input-card-initial-balance"
                  value={f.initial_balance}
                  onChange={(e) =>
                    setF({ ...f, initial_balance: e.target.value })
                  }
                  placeholder="10.00"
                />
              </div>
              <div className="grid gap-1">
                <Label>Type de carte</Label>
                <Select
                  value={f.card_type}
                  onValueChange={(v) => setF({ ...f, card_type: v })}
                >
                  <SelectTrigger data-testid="select-card-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VISA">VISA</SelectItem>
                    <SelectItem value="MASTERCARD">MASTERCARD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label>Catégorie</Label>
                <Select
                  value={f.category}
                  onValueChange={(v) => setF({ ...f, category: v })}
                >
                  <SelectTrigger data-testid="select-card-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERSONAL">
                      Particulier (PERSONAL)
                    </SelectItem>
                    <SelectItem value="PROFESSIONAL">
                      Professionnel (PROFESSIONAL)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label>Gamme</Label>
                <div
                  data-testid="input-card-grade"
                  className="flex h-9 items-center justify-between rounded-md border border-input bg-muted/40 px-3 py-1 text-xs"
                >
                  <span className="font-semibold">PREMIUM</span>
                  <span className="text-[10px] text-muted-foreground">
                    Standard / Basic indisponible
                  </span>
                </div>
              </div>
            </div>
            {confirm ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="mb-2 font-semibold">
                  Confirmer l'émission ? Le montant du devis sera débité de
                  votre wallet.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirm(false)}
                  >
                    Retour
                  </Button>
                  <Button
                    size="sm"
                    data-testid="button-confirm-issue"
                    disabled={busy}
                    onClick={() => void issue()}
                  >
                    {busy && (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    )}
                    Confirmer
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                data-testid="button-issue-card"
                disabled={!valid || !quote.data || quote.loading}
                onClick={() => setConfirm(true)}
              >
                Continuer
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SENSITIVE = [
  "pan",
  "card_number",
  "number",
  "cvv",
  "cvc",
  "pin",
  "expiry",
  "expiration",
  "exp_month",
  "exp_year",
];
const OPS = [
  ["topup", "Recharger", true],
  ["withdraw", "Retirer", true],
  ["enabled", "Activer", false],
  ["disabled", "Désactiver", false],
  ["terminate", "Résilier", false],
] as const;

/** Provider details live only in this component's state and are wiped on blur/hide/unmount. */
function CardDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [card, setCard] = useState<Rec | null>(null);
  const [txs, setTxs] = useState<Rec[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [cleared, setCleared] = useState(false);
  const ctrl = useRef<AbortController | null>(null);
  const [op, setOp] = useState<(typeof OPS)[number] | null>(null);
  const [amount, setAmount] = useState("");
  const [opBusy, setOpBusy] = useState(false);
  const [opError, setOpError] = useState("");
  const [opResult, setOpResult] = useState<Rec | null>(null);

  const wipe = useCallback(() => {
    ctrl.current?.abort();
    ctrl.current = null;
    setCard(null);
    setTxs([]);
    setRevealed(false);
    setLoading(false);
  }, []);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const ac = new AbortController();
    ctrl.current = ac;
    setLoading(true);
    setError("");
    setCleared(false);
    try {
      const r = await publicApi<Rec>(
        `/virtual-cards/${encodeURIComponent(id)}`,
        { cache: "no-store", signal: ac.signal },
      );
      if (ac.signal.aborted || ctrl.current !== ac) return;
      const data = rec(r.data);
      setCard(rec(data.card));
      setTxs(list(data.transactions));
    } catch (e) {
      if (!ac.signal.aborted)
        setError(
          e instanceof Error && e.name !== "AbortError"
            ? "Impossible de charger les détails de la carte."
            : "",
        );
    } finally {
      if (ctrl.current === ac) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const hide = () => {
      wipe();
      setCleared(true);
    };
    const vis = () => {
      if (document.visibilityState === "hidden") hide();
    };
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", vis);
    return () => {
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", vis);
      wipe();
    };
  }, [wipe]);

  const close = () => {
    wipe();
    onClose();
  };

  const runOp = async () => {
    if (!op) return;
    setOpBusy(true);
    setOpError("");
    try {
      const body = op[2] ? { action: op[0], amount } : { action: op[0] };
      const r = await publicApi<Rec>(
        `/virtual-cards/${encodeURIComponent(id)}/operations`,
        { method: "POST", body },
      );
      setOpResult(rec(r.data));
      setOp(null);
      setAmount("");
      onChanged();
      if (card) void load();
    } catch (e) {
      setOpError(errorMessage(e));
    } finally {
      setOpBusy(false);
    }
  };

  const secretEntries = card
    ? Object.entries(card).filter(
        ([k, v]) =>
          SENSITIVE.some((s) => k.toLowerCase().includes(s)) &&
          !k.startsWith("masked") &&
          v !== null &&
          typeof v !== "object",
      )
    : [];
  const safeCard = card
    ? Object.fromEntries(
        Object.entries(card).filter(
          ([k]) =>
            !SENSITIVE.some((s) => k.toLowerCase().includes(s)) ||
            k.startsWith("masked"),
        ),
      )
    : {};
  const amountOk = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto sm:max-w-xl"
        data-testid="dialog-card-detail"
      >
        <DialogHeader>
          <DialogTitle>Carte virtuelle</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">
            {id}
          </DialogDescription>
        </DialogHeader>
        {!card ? (
          <div className="space-y-3">
            {cleared && (
              <p className="text-xs text-muted-foreground">
                Détails effacés car la page a perdu le focus.
              </p>
            )}
            {error && <ErrorBox message={error} onRetry={() => void load()} />}
            <Button
              data-testid="button-load-card-details"
              disabled={loading}
              onClick={() => void load()}
            >
              {loading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-1.5 h-4 w-4" />
              )}
              Afficher les détails fournisseur
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#061a45] p-5 text-white">
              <p
                className="font-mono text-lg tracking-widest"
                data-testid="text-card-masked"
              >
                {txt(card.masked_pan, "**** **** **** ****")}
              </p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-300">
                <span>{txt(card.currency)}</span>
                <Chip value={card.status} />
              </div>
            </div>
            <MetaList data={safeCard} />
            {secretEntries.length > 0 && (
              <div className="rounded-lg border p-3">
                {revealed ? (
                  <dl
                    className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-sm"
                    data-testid="panel-card-secrets"
                  >
                    {secretEntries.map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="select-all">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Données sensibles masquées.
                  </p>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  data-testid="button-reveal-secrets"
                  onClick={() => setRevealed((r) => !r)}
                >
                  {revealed ? (
                    <EyeOff className="mr-1.5 h-3.5 w-3.5" />
                  ) : (
                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  {revealed ? "Masquer" : "Révéler PAN / CVV / PIN"}
                </Button>
              </div>
            )}
            <div>
              <p className="mb-2 text-xs font-semibold">Transactions</p>
              {txs.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Aucune transaction.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {txs.map((t, i) => (
                    <li
                      key={txt(t.id, String(i))}
                      className="flex justify-between gap-2 rounded border px-2.5 py-1.5 text-xs"
                    >
                      <span className="truncate">
                        {txt(
                          t.description ?? t.merchant ?? t.type,
                          "Opération",
                        )}
                      </span>
                      <span className="font-mono">
                        {txt(t.amount)} {txt(t.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                wipe();
              }}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Effacer les détails
            </Button>
          </div>
        )}

        <div className="border-t pt-4">
          <p className="mb-2 text-xs font-semibold">Opérations</p>
          {opResult && (
            <div className="mb-2 rounded-lg bg-emerald-50 p-2 text-xs">
              <MetaList data={opResult} />
            </div>
          )}
          {opError && (
            <div className="mb-2">
              <ErrorBox message={opError} />
            </div>
          )}
          {op ? (
            <div
              className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs"
              data-testid="panel-card-op-confirm"
            >
              <p className="font-semibold">
                {op[0] === "terminate"
                  ? "Résilier définitivement cette carte ? Action irréversible."
                  : `Confirmer : ${op[1]}`}
              </p>
              {op[2] && (
                <Input
                  inputMode="decimal"
                  placeholder="Montant (USD)"
                  data-testid="input-card-op-amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setOp(null);
                    setAmount("");
                  }}
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  variant={op[0] === "terminate" ? "destructive" : "default"}
                  data-testid="button-card-op-confirm"
                  disabled={opBusy || (op[2] && !amountOk)}
                  onClick={() => void runOp()}
                >
                  {opBusy && (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  )}
                  Confirmer
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {OPS.map((o) => (
                <Button
                  key={o[0]}
                  size="sm"
                  variant={o[0] === "terminate" ? "destructive" : "outline"}
                  data-testid={`button-card-op-${o[0]}`}
                  onClick={() => {
                    setOpResult(null);
                    setOpError("");
                    setOp(o);
                  }}
                >
                  {o[1]}
                </Button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
